import { FC, Fragment } from 'react'
import { partition } from 'lodash'

import {
  ProgressSteps,
  ProgressStepSkeleton,
  Separator,
  VStack,
  Widget,
} from '@revolut-internal/ui-kit'

import { CustomPlaceholder as Placeholder } from 'components/Placeholder/Placeholder'
import { useAlertingIncidentsTimelinesQuery } from 'modules/api/alerting/queries'
import { Security } from 'modules/security/security'
import { ResourcesPanelSection } from 'pages/Monitoring/components/ResourcesPanel/ResourcesPanelSection'
import {
  AlertingEntityType,
  AlertingPolicyGetResponse,
  IncidentTimelineResponse,
  ResourcesSideParams,
} from 'types'

import { QueueHealthIncidentsActions } from './QueueHealthIncidentsActions'
import { QueueHealthIncidentsSteps } from './QueueHealthIncidentsSteps'

type QueueHealthIncidentsProps = {
  resourcesSideParams: ResourcesSideParams
  policy?: AlertingPolicyGetResponse | null
  isPolicyPending: boolean
  onSetPolicy: VoidFunction
}

export const QueueHealthIncidents: FC<QueueHealthIncidentsProps> = ({
  resourcesSideParams,
  policy,
  isPolicyPending,
  onSetPolicy,
}) => {
  const { data: incidents, isPending } = useAlertingIncidentsTimelinesQuery(
    {
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      entityId: resourcesSideParams?.specialisation?.id!,
      entityType: AlertingEntityType.Specialisation,
    },
    {
      enabled: Boolean(resourcesSideParams?.specialisation),
    },
  )
  const [ongoingIncidents, pastIncidents] = partition<IncidentTimelineResponse>(
    incidents?.items,
    incident => !incident.resolvedDate,
  )

  const hasPolicy = Boolean(policy)

  if (isPending || isPolicyPending) {
    return (
      <ResourcesPanelSection isPending>
        <ProgressSteps variant="vertical-compact">
          <ProgressStepSkeleton />
          <ProgressStepSkeleton />
          <ProgressStepSkeleton />
        </ProgressSteps>
      </ResourcesPanelSection>
    )
  }

  return (
    <VStack space="s-16">
      {hasPolicy && ongoingIncidents.length ? (
        ongoingIncidents.map(incident => (
          <ResourcesPanelSection key={incident.incidentId} title="Ongoing">
            <QueueHealthIncidentsSteps state="done" timelines={incident.timelines} />
            <QueueHealthIncidentsActions incident={incident} />
          </ResourcesPanelSection>
        ))
      ) : (
        <Widget>
          <Placeholder
            icon="Binoculars"
            title={hasPolicy ? 'No ongoing incidents' : 'No policy set'}
            action={
              !hasPolicy && Security.canCreatePolicy
                ? {
                    name: 'Set policy',
                    onClick: onSetPolicy,
                  }
                : undefined
            }
          />
        </Widget>
      )}

      {Boolean(pastIncidents.length) && (
        <ResourcesPanelSection title="Past">
          <VStack>
            {pastIncidents.map((incident, index) => (
              <Fragment key={incident.incidentId}>
                <QueueHealthIncidentsSteps
                  state="default"
                  timelines={incident.timelines}
                />

                {index !== pastIncidents.length - 1 && <Separator px="s-12" />}
              </Fragment>
            ))}
          </VStack>
        </ResourcesPanelSection>
      )}
    </VStack>
  )
}
